/**
 * A component that introduces a random rotation to any element that it wraps
 * Suggest passing (only) positioning classes to this component via className="", if relevant. Mainly in the case of absolute positioning
 *
 * Note: Had to downgrade Framer JS from 6 to 4 because of this error (solution):
 * https://trustsu.com/reactjs/cant-import-the-named-export-children-from-non-ecmascript-module-only-default-export-is-available/
 *
 * If the "offset" prop is set to
 */

import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";

const WrapRotation = ({
  children,
  introDelay,
  introDirection,
  className,
  introZoomBack,
  rotationRange,
  offset,
}) => {
  // Set up random degree variables
  const [randomDegreesStart, setRandomDegreesStart] = useState(0);
  const [randomDegreesEnd, setRandomDegreesEnd] = useState(0);

  // Set translate values to animate from, if the prop is set
  const [introXValue, setIntroXValue] = useState(0);
  const [introYValue, setIntroYValue] = useState(0);

  // Reference for potential mouse tracking
  const containerRef = useRef(null);

  // TODO: The intro direction thing isn't working yet: setIntroXValue & setIntroYValue don't seem to be working inside the useEffect
  // Not exactly a priority...
  useEffect(() => {
    // console.log("introdir" + introDirection);
    // Determine initial values to animate from, if specified (default will not animate these)
    // Tried a switch case but didn't seem to work 🤷‍♂️
    if (introDirection === "fromTop") {
      setIntroYValue(-100);
      setIntroXValue(offset ? -50 : 0);
    } else if (introDirection === "fromRight") {
      setIntroYValue(offset ? -50 : 0);
      setIntroXValue(100);
    } else if (introDirection === "fromBottom") {
      setIntroYValue(100);
      setIntroXValue(offset ? -50 : 0);
    } else if (introDirection === "fromLeft") {
      setIntroYValue(offset ? -50 : 0);
      setIntroXValue(-100);
      // console.log("introXValue" + introXValue);
    } else {
      setIntroYValue(offset ? -50 : 0);
      setIntroXValue(offset ? -50 : 0);
    }
    // console.log("introXValue inside useEffect" + introXValue);
  }, [introDirection, offset]); // TODO: check! Added as dependency to fix warning, not sure it's needed though

  // Initiate various values on load
  useEffect(() => {
    // 3 different possible rotation degree ranges (sm is default)
    if (rotationRange === "sm") {
      setRandomDegreesStart(Math.floor(Math.random() * 40) - 20);
      setRandomDegreesEnd(Math.floor(Math.random() * 6) - 3); // Range between -3 to 3
    }
    if (rotationRange === "md") {
      setRandomDegreesStart(Math.floor(Math.random() * 40) - 20);
      setRandomDegreesEnd(Math.floor(Math.random() * 20) - 10); // Range between -10 to 10
    }
    if (rotationRange === "lg") {
      setRandomDegreesStart(Math.floor(Math.random() * 40) - 20);
      setRandomDegreesEnd(Math.floor(Math.random() * 40) - 20); // Range between -20 to 20
    }
  }, [rotationRange]); // TODO: check! Added as dependency to fix warning, not sure it's needed though

  // TODO: Mouse move effects (WIP)
  // useEffect(() => {
  //   let multiplierWidth;
  //   let multiplierHeight;
  //   let value1;
  //   let value2;

  //   const updateText = e => {
  //     multiplierWidth = e.offsetX / window.innerWidth;
  //     multiplierHeight = e.offsetY / window.innerHeight;
  //     value1 = multiplierWidth * 50;
  //     value2 = multiplierHeight * 20;
  //     // randomWidth = multiplierHeight * (200 - 100) + 100;
  //     containerRef.current.style.transform =
  //       "rotateY(" +
  //       value1 +
  //       "deg) rotate(" +
  //       value1 +
  //       "deg) perspective(" +
  //       400 +
  //       value2 +
  //       "px)";
  //   };
  //   window.addEventListener("mousemove", updateText);
  //   return () => window.removeEventListener("mousemove", updateText);
  // }, []);

  // TODO: Intro direction (WIP)
  // const variants = {
  //   initial: () => {

  //     switch (introDirection) {
  //       case 'fromTop':
  //         return {
  //           translateY: '-100%',
  //         };
  //       case 'fromRight':
  //         return {
  //           translateX: '100%',
  //         };
  //       case 'fromBottom':
  //         return {
  //           translateY: '100%',
  //         };
  //       case 'fromLeft':
  //         return {
  //           translateX: '-100%',
  //         };
  //       default:
  //         return {
  //           translateX: '-100%',
  //         };
  //     }
  //   },
  //   animate: {
  //     scale: 1,
  //     translateX: '0%',
  //     translateY: '0%',
  //     opacity: 1,
  //     rotate: randomDegreesEnd,
  //   },
  //   hover: () => {

  //   },
  // };

  return (
    <AnimatePresence>
      <motion.div
        className={classNames("", className)}
        initial={{
          opacity: 0,
          rotate: randomDegreesStart,
          // Only zoom in if specified
          scale: introZoomBack ? 0.4 : 1,
          // Calculate initial x & y translation value. If "offset", remove 50(%)
          translateX: offset ? introXValue - 50 + "%" : introXValue + "%",
          translateY: offset ? introYValue - 50 + "%" : introYValue + "%",
        }}
        animate={{
          opacity: 1,
          rotate: randomDegreesEnd,
          scale: 1,
          translateX: offset ? "-50%" : 0,
          translateY: offset ? "-50%" : 0,
          // transitionEnd: { rotate: randomDegreesEnd() },
        }}
        exit={{ opacity: 0 }}
        // easeOutExpo: 0.16, 1, 0.3, 1
        transition={{
          delay: introDelay,
          duration: 1,
          ease: [0.87, 0, 0.13, 1],
        }}

        // initial={'initial'}
        // animate={'animate'}
        // exit={'exit'}
        // variants={variants}
      >
        <div ref={containerRef}>{children}</div>
      </motion.div>
    </AnimatePresence>
  );
};

WrapRotation.defaultProps = {
  introDirection: null,
  introDelay: 0,
  introZoomBack: false,
  rotationRange: "sm",
  offset: false,
};

WrapRotation.propTypes = {
  children: PropTypes.node,
  /** Set intro delay: number, seconds (default: 0) */
  introDelay: PropTypes.number,
  /** Set direction element comes in from (optional): fromTop, fromRight, fromBottom, fromLeft */
  introDirection: PropTypes.string,
  /** Whether a "zoom in" intro style should be used. Default: false */
  introZoomBack: PropTypes.bool,
  /** If the "offset" prop is set to true (default: false), items will be offset -50% on translate x & y
   *  Useful when positioning absolutely to the center point of the item
   */
  offset: PropTypes.bool,
  /** Rotation range: sm (default), md or lg.
   * sm: between -3 & 3 degrees
   * md: between -10 & 10 degrees
   * lg: between -20 & 20 degrees
   */
  rotationRange: PropTypes.string,
};

export default WrapRotation;
